<ion-header>
  <ion-toolbar>
    <ion-title>Escalation Details</ion-title>
    <ion-buttons slot="end">
      <ion-button (click)="dismiss()">
        <ion-icon name="close"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content class="ion-padding">
  <div *ngIf="escalation">
    <!-- Header Section -->
        <div class="header-row">
          <div class="title-section">
            <h2 class="escalation-title">{{ escalation.title }}</h2>
            <p class="escalation-type" *ngIf="escalation.escalationType">
              {{ escalation.escalationType.name }}
            </p>
          </div>
          <div class="status-section">
            <ion-badge 
              [color]="getStatusColor(escalation.status)"
              class="status-badge"
              (click)="changeEscalationStatus($event)"
            >
              <ion-icon [name]="getStatusIcon(escalation.status)"></ion-icon>
              {{ ESCALATION_STATUS_LABELS[escalation.status] }}
            </ion-badge>
          </div>
        </div>
        <div class="action-buttons-container" [hidden]="escalation.status === ESCALATION_STATUSES.RESOLVED">
          <!-- Acknowledge Button (for pending status) -->
          <ion-button
            *ngIf="escalation.status === ESCALATION_STATUSES.PENDING"
            expand="block"
            color="primary"
            (click)="acknowledgeEscalation()"
            [disabled]="isLoading"
          >
            <ion-icon name="checkmark-circle" slot="start"></ion-icon>
            Acknowledge
          </ion-button>
    
          <!-- Resolve Button (for acknowledged status) -->
          <ion-button
            *ngIf="escalation.status === ESCALATION_STATUSES.ACKNOWLEDGED"
            expand="block"
            color="success"
            (click)="resolveEscalation()"
            [disabled]="isLoading"
          >
            <ion-icon name="checkmark-done-circle" slot="start"></ion-icon>
            Resolve
          </ion-button>
        </div>

    <!-- Description Section -->
    <ion-card *ngIf="escalation.body">
      <ion-card-header>
        <ion-card-title>Description</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <div [innerHTML]="escalation.body | safeHtml" class="description-content"></div>
      </ion-card-content>
    </ion-card>

    <!-- Public Note Section -->
    <ion-card *ngIf="escalation.publicNote">
      <ion-card-header>
        <ion-card-title>Public Note</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <div [innerHTML]="escalation.publicNote | safeHtml" class="public-note-content"></div>
      </ion-card-content>
    </ion-card>

    <!-- Details Section -->
    <ion-card>
      <ion-card-header>
        <ion-card-title>Details</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <div class="details-grid">
          <!-- Priority -->
          <div class="detail-item">
            <label>Priority</label>
            <ion-chip
              mode="ios"
              class="select-chip select-outline no-margin input-field"
              [outline]="true"
              (click)="changeEscalationPriority($event)"
            >
              <ion-label>
                <ng-container *ngIf="displayPriorityEmoji(escalation.priority) | colonToEmoji as emojiString">
                  <img
                    *ngIf="isUrl(emojiString)"
                    [src]="emojiString"
                    alt="Priority Emoji"
                    style="width: 1em; height: 1em; vertical-align: -0.125em; margin-right: 4px"
                  />
                  <span *ngIf="!isUrl(emojiString)" style="margin-right: 4px">{{ emojiString }}</span>
                </ng-container>
                <ng-template #priorityIcon *ngIf="getIconForPriority(escalation.priority)">
                  <ion-icon
                    [name]="getIconForPriority(escalation.priority)"
                    style="margin-right: 4px"
                  ></ion-icon>
                </ng-template>
                {{ displayPriority() }}
                <ion-icon name="chevron-down-outline" class="icon-padded-left button-icon"></ion-icon>
              </ion-label>
            </ion-chip>
          </div>

          <!-- Assignee -->
          <div class="detail-item">
            <label>Assignee</label>
            <ion-chip
              mode="ios"
              class="select-chip select-outline no-margin input-field"
              [outline]="true"
              (click)="changeEscalationAssignee($event)"
            >
                <ng-container *ngIf="escalation.assignedToUser; else groupOrUnassigned">
                  <app-avatar
                    size="16"
                    [name]="escalation.assignedToUser.name"
                    [image]="escalation.assignedToUser.photo"
                    [style.width]="'16px'"
                    [style.min-width]="'16px'"
                    [style.height]="'16px'"
                    [style.margin-right]="'4px'"
                  ></app-avatar>
                  {{ escalation.assignedToUser.name }}
                </ng-container>
                <ng-template #groupOrUnassigned>
                  <ng-container *ngIf="escalation.assignedToGroup; else unassigned">
                    <ion-icon name="people" style="margin-right: 4px"></ion-icon>
                    {{ escalation.assignedToGroup.name }}
                  </ng-container>
                  <ng-template #unassigned>
                    <ion-icon name="person" style="margin-right: 4px; opacity: 0.5"></ion-icon>
                    <span style="opacity: 0.5">Unassigned</span>
                  </ng-template>
                </ng-template>
                <ion-icon name="chevron-down-outline" class="icon-padded-left button-icon"></ion-icon>
            </ion-chip>
          </div>

          <!-- Channel -->
          <div class="detail-item" *ngIf="escalation.triageThread?.initialMessage?.slackChannel">
            <label>Channel</label>
            <div class="channel-display clickable" (click)="openTriageInSlack(
              escalation.triageThread.initialMessage.ts,
              escalation.triageThread.initialMessage.channelId
            )">
              <ion-icon name="logo-slack" style="margin-right: 8px"></ion-icon>
              <span>#{{ escalation.triageThread.initialMessage.slackChannel.name }}</span>
            </div>
          </div>

          <!-- Updated Date -->
          <div class="detail-item">
            <label>Last Updated</label>
            <div>{{ displayDateFrom(escalation.updatedAt) }}</div>
          </div>
        </div>
      </ion-card-content>
    </ion-card>

    <!-- Slack Channel Section -->
    <ion-card *ngIf="escalation.triageThread?.initialMessage">
      <ion-card-header>
        <ion-card-title>Slack Channel</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <div class="slack-channel-info">
          <ion-icon name="logo-slack" style="margin-right: 8px"></ion-icon>
          <a 
            (click)="openTriageInSlack(
              escalation.triageThread.initialMessage.ts,
              escalation.triageThread.initialMessage.channelId
            )"
            class="slack-link"
          >
            #{{ escalation.triageThread.initialMessage.slackChannel?.name || 'private-channel' }}
          </a>
        </div>
      </ion-card-content>
    </ion-card>

    <!-- Timeline Section -->
    <ion-card>
      <ion-card-header>
        <ion-card-title>Timeline</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <div class="timeline">
          <div 
            *ngFor="let event of timelineEvents; trackBy: trackByEvent" 
            class="timeline-event"
          >
            <div class="timeline-icon">
              <ion-icon 
                [name]="getTimelineIcon(event.type)"
                [color]="getTimelineColor(event.type)"
              ></ion-icon>
            </div>
            <div class="timeline-content">
              <div class="timeline-label">{{ event.data.label }}</div>
              <div class="timeline-description" *ngIf="event.data.description">
                {{ event.data.description }}
              </div>
              <div class="timeline-timestamp">{{ displayDateFrom(event.timestamp) }}</div>
            </div>
          </div>
        </div>
      </ion-card-content>
    </ion-card>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-overlay">
    <ion-spinner name="crescent"></ion-spinner>
    <p>Updating...</p>
  </div>
</ion-content> 